import React, { useContext, useMemo, useRef, useState } from 'react';
import DashboardHeader from '../../Components/DashboardHeader/DashboardHeader';
import Notification from '../../Components/Dashboard/Notification/Notification';
import AuthContext from '../../Context/AuthContext';
import { submitSupportRequest } from '../../api';
import { toast } from 'react-toastify';
import ClipIcon from '../../Assets/Images/clip.svg';

const MAX_MESSAGE_LENGTH = 2000;
const MAX_ATTACHMENTS = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const Support = () => {
  const authCtx = useContext(AuthContext);
  const [subject, setSubject] = useState('Support Request – Consultant Portal');
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const fileInputRef = useRef(null);

  const resolvedName = useMemo(() => {
    const storedName = localStorage.getItem('userName');
    if (authCtx?.userName && authCtx?.userName !== 'null null') {
      return authCtx.userName;
    }
    return storedName || '';
  }, [authCtx?.userName]);

  const resolvedEmail = useMemo(() => {
    return authCtx?.email || localStorage.getItem('email') || '';
  }, [authCtx?.email]);

  const userInitial = useMemo(() => {
    const source = (resolvedName || resolvedEmail || '').trim();
    return source ? source.charAt(0).toUpperCase() : 'S';
  }, [resolvedEmail, resolvedName]);

  const formatFileSize = (size) => {
    if (size >= 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${Math.round(size / 1024)} KB`;
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    let updated = [...attachments];
    let limitHit = false;
    files.forEach((file) => {
      if (updated.length >= MAX_ATTACHMENTS) {
        limitHit = true;
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is larger than 5 MB.`);
        return;
      }
      updated.push({
        id: `${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`,
        file,
      });
    });

    if (limitHit) {
      toast.info(`You can attach up to ${MAX_ATTACHMENTS} files.`);
    }

    setAttachments(updated);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((item) => item.id !== id));
  };

  const handleNotificationOpen = () => setIsNotificationOpen(true);
  const handleNotificationClose = () => setIsNotificationOpen(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    if (!message.trim()) {
      setFormError('Please share a few details so we can assist you better.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await submitSupportRequest({
        name: resolvedName,
        email: resolvedEmail,
        subject: subject.trim(),
        message: message.trim(),
        attachments: attachments.map((item) => item.file),
        orgId: authCtx?.orgId,
      });

      if (response?.status === 'success' || response?.statusCode === 200) {
        toast.success('Support request sent. We will reply soon.');
        setMessage('');
        setAttachments([]);
      } else if (response?.message) {
        toast.error(response.message);
      } else {
        toast.error('Unable to send request. Please try again.');
      }
    } catch (error) {
      console.error('Support request failed', error);
      toast.error('Something went wrong. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="dashboard-platform support-page">
      <DashboardHeader heading="Support" handleNotificationOpen={handleNotificationOpen} />
      <Notification
        isNotificationOpen={isNotificationOpen}
        handleNotificationClose={handleNotificationClose}
      />

      <div className="dashboard-container">
        <div className="support-shell">
          <p className="support-shell__title">Contact Support</p>
          <section className="support-card">

            <header className="support-card__header">
              <p className="support-card__lede">
                We're here to help. Ask a question, report an issue, or share feedback.
              </p>
            </header>

            <form className="support-form" onSubmit={handleSubmit}>
              <div className="support-field-row">
                <label className="support-field" htmlFor="support-name">
                  <span className='form-fields'>Full Name</span>
                  <input id="support-name" type="text" value={resolvedName} disabled readOnly />
                </label>
                <label className="support-field" htmlFor="support-email">
                  <span className='form-fields'>Email</span>
                  <input id="support-email" type="email" value={resolvedEmail} disabled readOnly />
                </label>
              </div>

              <div className="support-field support-field--subject" htmlFor="support-subject">
                <span className='form-fields'>Subject:</span>
                <input
                  id="support-subject"
                  type="text"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="Support Request – Consultant Portal"
                />
              </div>

              <div className="support-field support-field--message">
                <div className="support-field__heading">
                  <span>How can we help?</span>
                  <p style={{ fontSize: "16px" }}>Please share any relevant information that will help us assist you. Include details like report name, user, or steps taken.</p>
                </div>
                <textarea
                  id="support-message"
                  value={message}
                  onChange={(event) => {
                    setMessage(event.target.value);
                    if (formError) {
                      setFormError('');
                    }
                  }}
                  maxLength={MAX_MESSAGE_LENGTH}
                  placeholder="Type your message here..."
                  rows={5}
                />
                <div className="support-field__meta">
                  {formError ? (
                    <span className="support-field__error">{formError}</span>
                  ) : null}
                </div>

                <div className="support-attachments">
                  <div className="support-attachments__copy">
                    <label htmlFor="support-attachments-input" className="support-attachments__label">
                      <img aria-hidden="true" src={ClipIcon} alt="Clip" />
                      Attach files (optional)
                    </label>
                    <input
                      id="support-attachments-input"
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      ref={fileInputRef}
                    />
                    <span className="support-attachments__hint">Up to {MAX_ATTACHMENTS} files. 5 MB each.</span>
                  </div>
                  <span className="support-field__counter">{message.length}/{MAX_MESSAGE_LENGTH}</span>
                </div>
              </div>


              {attachments.length > 0 && (
                <ul className="support-attachments__list">
                  {attachments.map((attachment) => (
                    <li key={attachment.id}>
                      <div>
                        <p>{attachment.file.name}</p>
                        <span>{formatFileSize(attachment.file.size)}</span>
                      </div>
                      <button type="button" onClick={() => removeAttachment(attachment.id)}>
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="support-actions">
                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Support;
